/**
 * Helper de partage — Phase 1, incrément 9.
 *
 * Wrapper autour de `navigator.share` (Web Share API) avec fallback
 * gracieux : copie du texte dans le presse-papier si l'API n'est pas
 * disponible, puis alert d'information à l'utilisateur. En Phase 7,
 * Capacitor sera prioritaire pour le natif iOS/Android.
 */

export interface ShareInput {
  title: string;
  text: string;
  /** URL à partager (optionnel — si non fourni, seul le `text` est partagé). */
  url?: string;
}

export interface ShareResult {
  /** `'shared'` = navigator.share a réussi, `'copied'` = fallback clipboard,
   *  `'cancelled'` = l'utilisateur a annulé, `'unsupported'` = ni share ni clipboard. */
  via: 'shared' | 'copied' | 'cancelled' | 'unsupported';
}

function safeNavigator(): Navigator | undefined {
  return typeof navigator === 'undefined' ? undefined : navigator;
}

export async function share(input: ShareInput): Promise<ShareResult> {
  const nav = safeNavigator();
  // Web Share API
  if (nav && typeof nav.share === 'function') {
    try {
      await nav.share({
        title: input.title,
        text: input.text,
        ...(input.url ? { url: input.url } : {}),
      });
      return { via: 'shared' };
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        return { via: 'cancelled' };
      }
      // Si navigator.share lance pour d'autres raisons (NotAllowedError sur
      // contexte non-secure par ex.), on tente le fallback clipboard.
    }
  }
  // Fallback : clipboard
  const payload = input.url ? `${input.text}\n${input.url}` : input.text;
  try {
    if (nav?.clipboard?.writeText) {
      await nav.clipboard.writeText(payload);
      return { via: 'copied' };
    }
  } catch {
    /* clipboard refusé — on tombe sur unsupported */
  }
  return { via: 'unsupported' };
}
